import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpService } from 'app/services/http.service';
import { MessageService } from 'primeng/api';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-soporte',
  templateUrl: './soporte.component.html',
  styleUrls: ['./soporte.component.scss']
})
export class SoporteComponent implements OnInit {

  usuario: any;
  systemLogs: any[] = [];
  pendingRequests: any[] = [];
  loading: boolean = false;
  loadingPending: boolean = false;
  loadingRequest: boolean = false;

  // Datos para los dropdowns
  empresas: any[] = [];
  clientes: any[] = [];
  instalaciones: any[] = [];
  supervisores: any[] = [];

  // Opciones de tipo de app
  tiposApp: any[] = [
    { label: 'CONTROL GUARD', value: 'rondas' },
    { label: 'CONTROL ACCESOS', value: 'accesos' },
    { label: 'SUPERVISOR', value: 'supervisor' }
  ];
  selectedTipoApp: any = null;

  // Selecciones
  selectedEmpresa: any = null;
  selectedCliente: any = null;
  selectedInstalacion: any = null;
  selectedSupervisor: any = null;

  constructor(
    private messageService: MessageService,
    private http: HttpService,
    private cdr: ChangeDetectorRef
  ) {
    this.usuario = JSON.parse(this.http.getLocalstorage('usuario'));
  }

  async ngOnInit(): Promise<void> {
    await this.loadEmpresas();
    await Promise.all([
      this.getSystemLogs(),
      this.getPendingRequests()
    ]);
  }

  async loadEmpresas(): Promise<void> {
    try {
      this.empresas = await this.http.getEmpresaService();
    } catch (error) {
      console.error('Error al cargar empresas:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar las empresas'
      });
    }
  }

  onTipoAppChange(): void {
    // Limpiar todas las selecciones dependientes
    this.selectedEmpresa = null;
    this.selectedCliente = null;
    this.selectedInstalacion = null;
    this.selectedSupervisor = null;
    this.clientes = [];
    this.instalaciones = [];
    this.supervisores = [];
  }

  isSupervisorApp(): boolean {
    return this.selectedTipoApp && this.selectedTipoApp.value === 'supervisor';
  }

  canSolicitarLogs(): boolean {
    if (!this.selectedTipoApp || !this.selectedEmpresa) return false;
    if (this.isSupervisorApp()) {
      return !!this.selectedSupervisor;
    }
    return !!this.selectedInstalacion;
  }

  async onEmpresaChange(): Promise<void> {
    // Limpiar selecciones dependientes
    this.selectedCliente = null;
    this.selectedInstalacion = null;
    this.selectedSupervisor = null;
    this.clientes = [];
    this.instalaciones = [];
    this.supervisores = [];

    if (!this.selectedEmpresa) {
      return;
    }

    try {
      if (this.isSupervisorApp()) {
        this.supervisores = await this.http.getsupervisoresbyEmpresaService(this.selectedEmpresa._id);
      } else {
        this.clientes = await this.http.getclientesbyEmpresaService(this.selectedEmpresa._id);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.isSupervisorApp() ? 'No se pudieron cargar los supervisores' : 'No se pudieron cargar los clientes'
      });
    }
  }

  async onClienteChange(): Promise<void> {
    // Limpiar selección de instalación
    this.selectedInstalacion = null;
    this.instalaciones = [];

    if (!this.selectedCliente) {
      return;
    }

    try {
      this.instalaciones = await this.http.instalacionesByIdClienteService(this.selectedCliente._id);
    } catch (error) {
      console.error('Error al cargar instalaciones:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar las instalaciones'
      });
    }
  }

  onInstalacionChange(): void {
    // Método para manejar el cambio de instalación si es necesario
  }

  async solicitarLogs(): Promise<void> {
    if (!this.canSolicitarLogs()) {
      return;
    }

    this.loadingRequest = true;
    try {
      let url: string;
      if (this.isSupervisorApp()) {
        url = `${environment.rutaApi}request-logs-supervisor/${this.selectedSupervisor._id}`;
      } else {
        url = `${environment.rutaApi}request-logs/${this.selectedInstalacion._id}?tipo=${this.selectedTipoApp.value}`;
      }
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        this.messageService.add({
          severity: 'success',
          summary: 'Solicitud exitosa',
          detail: `Solicitud de logs enviada a ${this.selectedTipoApp.label}`
        });
        console.log('Respuesta de solicitud de logs:', data);

        // Recargar solicitudes pendientes
        setTimeout(() => {
          this.getPendingRequests();
        }, 1000);
      } else {
        throw new Error(`Error en la solicitud: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al solicitar logs:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo realizar la solicitud de logs'
      });
    } finally {
      this.loadingRequest = false;
    }
  }

  async getPendingRequests(): Promise<void> {
    this.loadingPending = true;
    try {
      const res = await this.http.getPendingLogRequestsService();
      const pendientes = (res && res.pendientes) ? res.pendientes : [];
      const pendientesSupervisores = (res && res.pendientesSupervisores) ? res.pendientesSupervisores : [];
      // Aplanar: una fila por cada tipo de solicitud pendiente
      this.pendingRequests = [];
      pendientes.forEach(item => {
        if (item.SolicitudLogs) {
          this.pendingRequests.push({
            ...item,
            _tipoApp: 'Control Guard',
            _fechaSolicitud: item.SolicitudLogs
          });
        }
        if (item.SolicitudLogsAccesos) {
          this.pendingRequests.push({
            ...item,
            _tipoApp: 'Control Accesos',
            _fechaSolicitud: item.SolicitudLogsAccesos
          });
        }
      });
      pendientesSupervisores.forEach(item => {
        if (item.SolicitudLogs) {
          this.pendingRequests.push({
            ...item,
            _tipoApp: 'Supervisor',
            _fechaSolicitud: item.SolicitudLogs
          });
        }
      });
      console.log('pendingRequests', this.pendingRequests);
    } catch (error) {
      console.error('Error al cargar solicitudes pendientes:', error);
    } finally {
      this.loadingPending = false;
      this.cdr.detectChanges();
    }
  }

  async getSystemLogs(): Promise<void> {
    this.loading = true;
    try {
      this.systemLogs = await this.http.getSystemLogsService();
      console.log('systemLogs', this.systemLogs);
    } catch (error) {
      console.error('Error al cargar system logs:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los registros'
      });
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  refreshAll(): void {
    this.getSystemLogs();
    this.getPendingRequests();
  }

  descargarLog(logFile: string) {
    console.log('Descargando log:', logFile);

    if (!logFile) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'No hay archivo de log disponible'
      });
      return;
    }

    // Construir la URL directa al archivo
    const url = `https://admin.controlguard.cl/assets/systemLogs/${logFile}`;

    // Crear un enlace temporal para descargar el archivo
    const link = document.createElement('a');
    link.href = url;
    link.download = logFile;
    link.target = '_blank';
    link.click();

    this.messageService.add({
      severity: 'success',
      summary: 'Descarga iniciada',
      detail: 'El archivo se está descargando'
    });
  }

  getAppName(tipo: string): string {
    if (!tipo) return 'Control Guard';
    switch (tipo.toLowerCase()) {
      case 'controlacceso':
        return 'Control Accesos';
      case 'controlronda':
        return 'Control Ronda';
      case 'supervisor':
        return 'Supervisor';
      default:
        return tipo;
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return 'N/A';

    const kb = bytes / 1024;
    if (kb < 1024) {
      return kb.toFixed(1) + ' KB';
    }

    const mb = kb / 1024;
    return mb.toFixed(2) + ' MB';
  }

}
